import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { PlatformLocation } from "@angular/common";
import { takeUntil } from "rxjs/operators";
import { ReplaySubject } from "rxjs";
import { CustomUserClaims } from "@core/interfaces/entities";

@Component({
    selector: "app-consent-modal",
    templateUrl: "./consent-modal.component.html",
    styleUrls: ["./consent-modal.component.scss"],
    standalone: false,
})
export class ConsentModalComponent implements OnInit, OnDestroy {
    public readonly translationPrefix = "common.modals.consentModal.";
    public consentForm: FormGroup = null;
    @Input() userData: CustomUserClaims;
    @Output() selectionComplete = new EventEmitter();
    private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

    static phoneNumberConditionallyRequired(formControl: AbstractControl) {
        return formControl.parent && formControl.parent.get("hasAcceptedConsent").value ? Validators.required(formControl) : null;
    }

    constructor(
        private fb: FormBuilder,
        public activeModal: NgbActiveModal,
        private location: PlatformLocation,
    ) {
        location.onPopState(() => activeModal.close());
    }

    private initForm(userData: CustomUserClaims) {
        this.consentForm = this.fb.group({
            firstName: [{ value: userData.displayFirstName, disabled: true }],
            email: [{ value: userData.email, disabled: true }],
            hasAcceptedConsent: [null, Validators.required],
            phoneNumber: userData.displayMobilePhone
                ? { value: userData.displayMobilePhone, disabled: true }
                : [
                      null,
                      {
                          validators: [ConsentModalComponent.phoneNumberConditionallyRequired],
                      },
                  ],
        });
    }

    ngOnInit(): void {
        this.initForm(this.userData);
        this.setUpFlow();
    }

    ngOnDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    get phoneNumber() {
        return this.consentForm.get("phoneNumber");
    }

    get hasAcceptedConsent() {
        return this.consentForm.get("hasAcceptedConsent");
    }

    private setConsentFC(consent: boolean) {
        this.hasAcceptedConsent.patchValue(consent);
    }

    submitSelection(consent: boolean) {
        this.setConsentFC(consent);
        if (this.consentForm.invalid) {
            return;
        }

        this.selectionComplete.emit({
            phoneNumber: this.phoneNumber.value ? this.phoneNumber.value.replace(/-/g, "") : null,
            hasAcceptedConsent: this.hasAcceptedConsent.value,
        });
        this.activeModal.close();
    }

    private setUpFlow() {
        this.hasAcceptedConsent.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((consent) => {
            if (!consent) {
                this.phoneNumber.patchValue(null);
            }
            this.phoneNumber.updateValueAndValidity();
        });
    }
}
